import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Loader2, XCircle } from "lucide-react";
import { BrandLogo } from "@/components/Brand";
import { computeIVA, fmtMXN } from "@/lib/iva";

const MAX_ATTEMPTS = 7;
const POLL_INTERVAL = 2000;

export default function BookSuccess() {
  const [params] = useSearchParams();
  const { slug } = useParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "checking", data: null, error: "" });
  const attemptRef = useRef(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId || !slug) {
      setState({ phase: "error", data: null, error: "Sesión no encontrada" });
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    async function poll() {
      try {
        const { data } = await api.get(`/t/${slug}/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data, error: "" });
          return;
        }
        if (data.status === "expired" || data.status === "complete" && data.payment_status === "unpaid") {
          setState({ phase: "expired", data, error: "El pago expiró o fue cancelado." });
          return;
        }
        attemptRef.current += 1;
        if (attemptRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data, error: "" });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setState({ phase: "error", data: null, error: "Error verificando el pago" });
      }
    }
    poll();
  }, [sessionId, slug]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-slate-200 glass-nav">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="book-success-logo">
            <BrandLogo />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        {state.phase === "checking" && (
          <Card className="border-slate-200" data-testid="book-success-checking">
            <CardContent className="p-12 text-center">
              <Loader2 className="h-10 w-10 mx-auto text-[hsl(var(--accent))] animate-spin" />
              <h1 className="font-display text-3xl font-light mt-6">Verificando tu pago…</h1>
              <p className="text-slate-600 mt-2">Esto tomará unos segundos.</p>
            </CardContent>
          </Card>
        )}

        {state.phase === "paid" && state.data?.appointment && (
          <Card className="border-slate-200" data-testid="book-success-paid">
            <CardContent className="p-10 sm:p-14 text-center">
              <div className="mx-auto h-16 w-16 rounded-full grid place-items-center text-white" style={{ backgroundColor: "#1E3A8A" }}>
                <Check className="h-7 w-7" />
              </div>
              <p className="overline mt-6">Pago confirmado</p>
              <h1 className="font-display text-4xl font-light mt-3">¡Tu cita está reservada!</h1>
              <p className="text-slate-600 mt-3">Te enviamos un correo a <span className="font-semibold">{state.data.appointment.client_email}</span></p>

              <div className="mt-8 inline-block rounded-xl border border-slate-200 bg-white p-6 text-left min-w-[300px]">
                <p className="overline">Detalle</p>
                <p className="font-display text-xl mt-2">{state.data.appointment.service_name}</p>
                <p className="text-sm text-slate-600 mt-1">{state.data.appointment.date} · {state.data.appointment.time}</p>
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-1 text-sm">
                  {(() => {
                    const price = state.data.appointment.full_price || state.data.appointment.service_price;
                    const b = computeIVA(price, true);
                    return (
                      <>
                        <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmtMXN(b.subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">IVA (16%)</span><span>{fmtMXN(b.iva)}</span></div>
                        <div className="flex justify-between font-medium pt-1 border-t border-slate-100"><span>Total del servicio</span><span>{fmtMXN(b.total)}</span></div>
                      </>
                    );
                  })()}
                  <div className="flex justify-between pt-2 mt-2 border-t border-slate-200"><span className="text-slate-500">Depósito pagado</span><span className="font-semibold text-[hsl(var(--accent))]">${state.data.appointment.deposit_amount.toFixed(2)}</span></div>
                  <div className="flex justify-between text-base font-semibold pt-1"><span>Restante en cita</span><span>${(state.data.appointment.balance_due || 0).toFixed(2)}</span></div>
                </div>
              </div>

              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Button variant="outline" onClick={() => navigate("/")} data-testid="success-home-btn">Ir al inicio</Button>
                <Button onClick={() => navigate(slug ? `/${slug}` : "/")} data-testid="success-new-btn">Agendar otra</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {state.phase === "expired" && (
          <Card className="border-slate-200" data-testid="book-success-expired">
            <CardContent className="p-12 text-center">
              <XCircle className="h-12 w-12 mx-auto text-red-600" />
              <h1 className="font-display text-3xl font-light mt-6">Pago no completado</h1>
              <p className="text-slate-600 mt-2">{state.error}</p>
              <Button className="mt-6" onClick={() => navigate(slug ? `/${slug}` : "/")} data-testid="expired-retry-btn">Intentar de nuevo</Button>
            </CardContent>
          </Card>
        )}

        {state.phase === "timeout" && (
          <Card className="border-slate-200" data-testid="book-success-timeout">
            <CardContent className="p-12 text-center">
              <Loader2 className="h-10 w-10 mx-auto text-slate-400" />
              <h1 className="font-display text-3xl font-light mt-6">El pago aún está procesándose</h1>
              <p className="text-slate-600 mt-2">Recibirás un correo cuando se confirme. Puedes cerrar esta pestaña.</p>
              <Button className="mt-6" variant="outline" onClick={() => window.location.reload()} data-testid="timeout-refresh-btn">Refrescar</Button>
            </CardContent>
          </Card>
        )}

        {state.phase === "error" && (
          <Card className="border-slate-200" data-testid="book-success-error">
            <CardContent className="p-12 text-center">
              <XCircle className="h-12 w-12 mx-auto text-red-600" />
              <h1 className="font-display text-3xl font-light mt-6">Error</h1>
              <p className="text-slate-600 mt-2">{state.error}</p>
              <Button className="mt-6" onClick={() => navigate(slug ? `/${slug}` : "/")} data-testid="error-retry-btn">Volver</Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
